import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Lock, Shield, Smartphone, UserPlus, KeyRound, Mail } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '../ui/input-otp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const { resetToken } = useParams();
  const [step, setStep] = useState('pin'); // pin, 2fa, setup, register, reset-request, reset-2fa
  const [pin, setPin] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [adminExists, setAdminExists] = useState(true);
  const [registerData, setRegisterData] = useState({ username: '', email: '' });

  useEffect(() => {
    checkAdminExists();
    // Check if we have a reset token in URL
    if (resetToken) {
      handleResetToken();
    }
  }, [resetToken]);

  const checkAdminExists = async () => {
    try {
      const response = await axios.get(`${API}/admin/check-exists`);
      setAdminExists(response.data.exists);
    } catch (error) {
      console.error('Error checking admin:', error);
    }
  };

  const handleResetToken = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/reset-2fa/${resetToken}`);
      setSetupData(response.data);
      setStep('reset-2fa');
      toast.success('Scan de nieuwe QR code met Microsoft Authenticator');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ongeldige of verlopen reset link');
      setStep('pin');
    } finally {
      setLoading(false);
    }
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    
    if (pin.length !== 4) {
      toast.error('PIN moet 4 cijfers zijn');
      return;
    }
    
    // Check if PIN is correct (master PIN: 1169)
    if (pin === '1169') {
      setStep('2fa');
    } else {
      toast.error('Ongeldige PIN');
    }
  };

  const handleLogin = async () => {
    if (totpCode.length !== 6) {
      toast.error('Voer de 6-cijferige code in');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, {
        pin,
        code: totpCode
      });
      
      localStorage.setItem('adminToken', response.data.token);
      toast.success('Welkom in het Admin Portal');
      navigate('/admin/dashboard');
    } catch (error) {
      if (error.response?.status === 401 && !adminExists) {
        // No admin with 2FA, setup required
        setStep('setup');
      } else {
        toast.error(error.response?.data?.detail || 'Inloggen mislukt');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    setLoading(true);
    try {
      // Use the new setup-2fa endpoint that works for both new and existing admins
      const response = await axios.post(`${API}/admin/setup-2fa`, {
        pin: pin,
        email: registerData.email || null
      });
      
      setSetupData(response.data);
      setStep('setup');
      toast.success('Scan de QR code met Microsoft Authenticator');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Setup mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!registerData.username || !registerData.email) {
      toast.error('Vul alle velden in');
      return;
    }
    
    if (pin.length !== 4) {
      toast.error('Voer eerst de master PIN in');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/register`, {
        username: registerData.username,
        pin: pin,
        email: registerData.email
      });
      
      setSetupData(response.data);
      setStep('setup');
      toast.success('Account aangemaakt! Scan de QR code met Microsoft Authenticator');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registratie mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/request-password-reset`);
      toast.success('Reset email verzonden naar de geregistreerde adressen');
      setStep('pin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset aanvraag mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (totpCode.length !== 6) {
      toast.error('Voer de 6-cijferige code in');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify-2fa`, {
        admin_id: setupData.admin_id,
        code: totpCode
      });
      
      toast.success('2FA ingeschakeld! Log opnieuw in.');
      setStep('2fa');
      setTotpCode('');
      setSetupData(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verificatie mislukt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'radial-gradient(circle at center, #2C1B18 0%, #050505 100%)' }}
      data-testid="admin-login"
    >
      <div className="glass rounded-2xl p-8 md:p-12 max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield size={32} className="text-[#D4AF37]" />
          </div>
          <h1 className="font-heading text-2xl text-white">Admin Portal</h1>
          <p className="text-[#A8A29E] text-sm mt-2">ZuiverParfum Management</p>
        </div>

        {/* PIN Step */}
        {step === 'pin' && (
          <form onSubmit={handlePinSubmit} className="space-y-6">
            <div className="text-center">
              <Lock size={24} className="mx-auto text-[#D4AF37] mb-3" />
              <p className="text-[#A8A29E] mb-4">Voer je PIN code in</p>
              <InputOTP
                maxLength={4}
                value={pin}
                onChange={setPin}
                className="justify-center"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} masked className="bg-[#1C1917] border-[#2C1B18] text-white text-xl" />
                  <InputOTPSlot index={1} masked className="bg-[#1C1917] border-[#2C1B18] text-white text-xl" />
                  <InputOTPSlot index={2} masked className="bg-[#1C1917] border-[#2C1B18] text-white text-xl" />
                  <InputOTPSlot index={3} masked className="bg-[#1C1917] border-[#2C1B18] text-white text-xl" />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <button
              type="submit"
              className="btn-primary w-full"
              data-testid="pin-submit"
            >
              Doorgaan
            </button>
            
            {/* Action buttons */}
            <div className="flex flex-col gap-2 pt-4 border-t border-[#2C1B18]">
              {!adminExists && (
                <button
                  type="button"
                  onClick={() => setStep('register')}
                  className="flex items-center justify-center gap-2 text-[#D4AF37] hover:text-white text-sm py-2 transition-colors"
                  data-testid="register-btn"
                >
                  <UserPlus size={16} />
                  Admin Account Registreren
                </button>
              )}
              <button
                type="button"
                onClick={() => setStep('reset-request')}
                className="flex items-center justify-center gap-2 text-[#A8A29E] hover:text-white text-sm py-2 transition-colors"
                data-testid="reset-btn"
              >
                <KeyRound size={16} />
                Wachtwoord / 2FA Reset
              </button>
            </div>
          </form>
        )}

        {/* Register Step */}
        {step === 'register' && (
          <div className="space-y-6">
            <div className="text-center">
              <UserPlus size={24} className="mx-auto text-[#D4AF37] mb-3" />
              <h2 className="text-white font-medium mb-2">Admin Registratie</h2>
              <p className="text-[#A8A29E] text-sm">
                Maak een nieuw admin account aan
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">Gebruikersnaam</label>
                <input
                  type="text"
                  value={registerData.username}
                  onChange={(e) => setRegisterData({...registerData, username: e.target.value})}
                  className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                  placeholder="admin"
                />
              </div>
              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">E-mail (voor reset)</label>
                <input
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                  className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                  placeholder="e.grant@live.nl"
                />
              </div>
              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">Master PIN</label>
                <InputOTP
                  maxLength={4}
                  value={pin}
                  onChange={setPin}
                  className="justify-center"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} masked className="bg-[#1C1917] border-[#2C1B18] text-white text-xl" />
                    <InputOTPSlot index={1} masked className="bg-[#1C1917] border-[#2C1B18] text-white text-xl" />
                    <InputOTPSlot index={2} masked className="bg-[#1C1917] border-[#2C1B18] text-white text-xl" />
                    <InputOTPSlot index={3} masked className="bg-[#1C1917] border-[#2C1B18] text-white text-xl" />
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>
            
            <button
              onClick={handleRegister}
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center"
              data-testid="register-submit"
            >
              {loading ? <div className="spinner"></div> : 'Account Aanmaken'}
            </button>
            <button
              onClick={() => setStep('pin')}
              className="text-[#A8A29E] hover:text-white text-sm w-full text-center"
            >
              ← Terug
            </button>
          </div>
        )}

        {/* Reset Request Step */}
        {step === 'reset-request' && (
          <div className="space-y-6 text-center">
            <div>
              <Mail size={24} className="mx-auto text-[#D4AF37] mb-3" />
              <h2 className="text-white font-medium mb-2">Wachtwoord / 2FA Reset</h2>
              <p className="text-[#A8A29E] text-sm">
                Er wordt een reset link gestuurd naar de geregistreerde e-mailadressen (e.grant@live.nl en noreply@zuiverparfum.nl)
              </p>
            </div>
            <button
              onClick={handleRequestReset}
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center"
              data-testid="request-reset-btn"
            >
              {loading ? <div className="spinner"></div> : 'Verstuur Reset Link'}
            </button>
            <button
              onClick={() => setStep('pin')}
              className="text-[#A8A29E] hover:text-white text-sm w-full text-center"
            >
              ← Terug naar Login
            </button>
          </div>
        )}

        {/* 2FA Step */}
        {step === '2fa' && !setupData && (
          <div className="space-y-6">
            <div className="text-center">
              <Smartphone size={24} className="mx-auto text-[#D4AF37] mb-3" />
              <p className="text-[#A8A29E] mb-4">
                Voer de 6-cijferige code in van Microsoft Authenticator
              </p>
              <InputOTP
                maxLength={6}
                value={totpCode}
                onChange={setTotpCode}
                className="justify-center"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                  <InputOTPSlot index={1} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                  <InputOTPSlot index={2} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                  <InputOTPSlot index={3} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                  <InputOTPSlot index={4} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                  <InputOTPSlot index={5} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <button
              onClick={handleLogin}
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center"
              data-testid="2fa-submit"
            >
              {loading ? <div className="spinner"></div> : 'Inloggen'}
            </button>
            
            {/* Link to setup Microsoft Authenticator */}
            <div className="pt-4 border-t border-[#2C1B18]">
              <button
                onClick={() => setStep('setup-email')}
                disabled={loading}
                className="flex items-center justify-center gap-2 text-[#D4AF37] hover:text-white text-sm py-2 w-full transition-colors"
              >
                <UserPlus size={16} />
                Microsoft Authenticator registreren
              </button>
            </div>
            
            <button
              onClick={() => setStep('pin')}
              className="text-[#A8A29E] hover:text-white text-sm w-full text-center"
            >
              ← Terug naar PIN
            </button>
          </div>
        )}

        {/* Setup Email Step - for 2FA registration */}
        {step === 'setup-email' && !setupData && (
          <div className="space-y-6">
            <div className="text-center">
              <Mail size={24} className="mx-auto text-[#D4AF37] mb-3" />
              <h2 className="text-white font-medium mb-2">Microsoft Authenticator Setup</h2>
              <p className="text-[#A8A29E] text-sm">
                Voer uw e-mailadres in voor het 2FA account
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">E-mailadres</label>
                <input
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                  className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                  placeholder="admin@zuiverparfum.nl"
                />
              </div>
              <p className="text-[#57534E] text-xs">
                Dit e-mailadres wordt getoond in Microsoft Authenticator zodat je het account kunt herkennen.
              </p>
            </div>
            
            <button
              onClick={handleSetup}
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center"
            >
              {loading ? <div className="spinner"></div> : 'Genereer QR Code'}
            </button>
            
            <button
              onClick={() => setStep('2fa')}
              className="text-[#A8A29E] hover:text-white text-sm w-full text-center"
            >
              ← Terug
            </button>
          </div>
        )}

        {/* Setup Step */}
        {step === 'setup' && !setupData && (
          <div className="space-y-6 text-center">
            <div>
              <Shield size={24} className="mx-auto text-[#D4AF37] mb-3" />
              <h2 className="text-white font-medium mb-2">2FA Instellen</h2>
              <p className="text-[#A8A29E] text-sm">
                Eerste login. Stel 2-factor authenticatie in voor beveiliging.
              </p>
            </div>
            <button
              onClick={handleSetup}
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center"
              data-testid="setup-btn"
            >
              {loading ? <div className="spinner"></div> : 'Setup Microsoft Authenticator'}
            </button>
          </div>
        )}

        {/* QR Code Setup (also used for reset) */}
        {(setupData && (step === 'setup' || step === 'reset-2fa')) && (
          <div className="space-y-6 text-center">
            <div>
              <h2 className="text-white font-medium mb-2">Scan QR Code</h2>
              <p className="text-[#A8A29E] text-sm mb-4">
                Open Microsoft Authenticator en scan deze code
              </p>
              <img 
                src={setupData.qr_code} 
                alt="QR Code" 
                className="mx-auto w-48 h-48 bg-white p-2 rounded-lg"
              />
              <p className="text-[#57534E] text-xs mt-2">
                Of voer handmatig in: {setupData.secret}
              </p>
            </div>
            <div>
              <p className="text-[#A8A29E] text-sm mb-3">
                Voer de 6-cijferige code in om te verifiëren
              </p>
              <InputOTP
                maxLength={6}
                value={totpCode}
                onChange={setTotpCode}
                className="justify-center"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                  <InputOTPSlot index={1} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                  <InputOTPSlot index={2} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                  <InputOTPSlot index={3} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                  <InputOTPSlot index={4} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                  <InputOTPSlot index={5} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <button
              onClick={handleVerify2FA}
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center"
              data-testid="verify-2fa-btn"
            >
              {loading ? <div className="spinner"></div> : 'Verifiëren & 2FA Inschakelen'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;
